import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { useSiteContent } from '../hooks/useSiteContent';
import { useSiteServices } from '../hooks/useSiteServices';
import { trackContactFormView, trackContactFormSubmit, trackPhoneClick } from '../hooks/useAnalytics';
import './Contact.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const { content: contactInfo, loading: contentLoading } = useSiteContent('contact_info');
  const { services } = useSiteServices();
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: '',
    gdprAccepted: false
  });

  const [submitStatus, setSubmitStatus] = useState(null);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Kontakt | Nessenius Garten & Landschaftsbau';
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Kontaktieren Sie Nessenius Garten & Landschaftsbau. Kostenloses Angebot anfordern. Telefon: 0176-55669090. Nörten-Hardenberg, Göttingen und 30km Umkreis.');
    // Track contact form view
    trackContactFormView();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.gdprAccepted) {
      alert('Bitte akzeptieren Sie die Datenschutzerklärung');
      return;
    }

    setSubmitStatus('loading');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email || null,
          service: formData.service,
          message: formData.message,
          gdpr_consent: formData.gdprAccepted,
          honeypot: '' // Leave empty for real submissions
        }),
      });

      if (!response.ok) {
        throw new Error('Submission failed');
      }

      const data = await response.json();
      setSubmitStatus('success');
      // Track successful submission
      trackContactFormSubmit();
      
      // Reset form
      setFormData({
        name: '',
        phone: '',
        email: '',
        service: '',
        message: '',
        gdprAccepted: false
      });

      setTimeout(() => setSubmitStatus(null), 8000);
      
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  return (
    <div className="contact-page">
      <section className="contact-hero">
        <div className="container">
          <h1>Kontakt</h1>
          <p>Wir freuen uns auf Ihre Nachricht und beraten Sie gerne</p>
        </div>
      </section>

      <section className="contact-content-section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Information */}
            <div className="contact-info">
              <div className="contact-info-card network-card">
                <h2 className="heading-3">Kontaktinformationen</h2>
                
                <div className="contact-info-list">
                  <a href={`tel:${contactInfo.phone}`} className="contact-info-item">
                    <div className="contact-icon">
                      <Phone size={24} />
                    </div>
                    <div>
                      <div className="contact-label">Telefon</div>
                      <div className="contact-value">{contactInfo.phone}</div>
                    </div>
                  </a>

                  <a href={`mailto:${contactInfo.email}`} className="contact-info-item">
                    <div className="contact-icon">
                      <Mail size={24} />
                    </div>
                    <div>
                      <div className="contact-label">E-Mail</div>
                      <div className="contact-value">{contactInfo.email}</div>
                    </div>
                  </a>

                  <div className="contact-info-item">
                    <div className="contact-icon">
                      <MapPin size={24} />
                    </div>
                    <div>
                      <div className="contact-label">Adresse</div>
                      <div className="contact-value">
                        {contactInfo.address}<br />
                        {contactInfo.zip_city}
                      </div>
                    </div>
                  </div>

                  <div className="contact-info-item">
                    <div className="contact-icon">
                      <Clock size={24} />
                    </div>
                    <div>
                      <div className="contact-label">Öffnungszeiten</div>
                      <div className="contact-value opening-hours">
                        <div className="hours-row"><span className="day">Mo - Fr:</span><span className="time">{contactInfo.opening_hours?.mo_fr || '08:00 - 18:00'}</span></div>
                        <div className="hours-row"><span className="day">Sa:</span><span className="time">{contactInfo.opening_hours?.sa || '09:00 - 14:00'}</span></div>
                        <div className="hours-row"><span className="day">So:</span><span className="time">{contactInfo.opening_hours?.so || 'Geschlossen'}</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Google Maps Embed */}
              <div className="map-container">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2471.5!2d9.9339!3d51.5735!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47bad5fce030d5ed%3A0xdd7e9e5b4a8cd4a7!2sNessenius%20Gartenservice!5e0!3m2!1sde!2sde!4v1708271234567"
                  width="100%"
                  height="300"
                  style={{ border: 0, borderRadius: '24px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Nessenius Garten & Landschaftsbau Standort"
                ></iframe>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-wrapper">
              <div className="contact-form-card network-card">
                <h2 className="heading-3">Angebot anfordern</h2>
                <p className="body-small" style={{ marginTop: '8px', color: 'var(--text-secondary)' }}>
                  Füllen Sie das Formular aus und wir melden uns zeitnah bei Ihnen
                </p>

                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="Ihr vollständiger Name"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone" className="form-label">Telefon *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="form-input"
                        placeholder="+49 ..."
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="email" className="form-label">E-Mail *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="form-input"
                        placeholder="ihre@email.de"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="service" className="form-label">Gewünschte Leistung</label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="form-input"
                    >
                      <option value="">Bitte wählen...</option>
                      {services.map(service => (
                        // Den lesbaren Namen speichern, nicht die ID: der Wert
                        // landet unveraendert in der Lead-Uebersicht und in der
                        // E-Mail an den Chef ("Gartenbau" statt "entsorgung").
                        <option key={service.id} value={service.name}>
                          {service.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message" className="form-label">Nachricht *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      className="form-textarea"
                      rows="5"
                      placeholder="Beschreiben Sie Ihr Anliegen..."
                    ></textarea>
                  </div>

                  <div className="form-checkbox-group">
                    <input
                      type="checkbox"
                      id="gdprAccepted"
                      name="gdprAccepted"
                      checked={formData.gdprAccepted}
                      onChange={handleChange}
                      required
                      className="form-checkbox"
                    />
                    <label htmlFor="gdprAccepted" className="form-checkbox-label">
                      Ich habe die <a href="/datenschutz" target="_blank">Datenschutzerklärung</a> zur Kenntnis genommen und bin damit einverstanden, dass die von mir angegebenen Daten zur Bearbeitung meiner Anfrage verarbeitet werden. *
                    </label>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="form-success">
                      Vielen Dank! Ihre Nachricht wurde erfolgreich versendet. Wir melden uns zeitnah bei Ihnen.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="form-error">
                      Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns telefonisch.
                    </div>
                  )}

                  <button 
                    type="submit" 
                    className="btn-cta form-submit-btn"
                    disabled={submitStatus === 'loading'}
                  >
                    <Send size={18} style={{ marginRight: '8px', display: 'inline-block', verticalAlign: 'middle' }} />
                    {submitStatus === 'loading' ? 'Wird gesendet...' : 'Anfrage senden'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
